type WikiGenerationAccessStateParams = {
  isPublicRepo: boolean;
  hasActiveSubscription?: boolean;
  isSettingsLoading: boolean;
};

export type WikiGenerationAccessState = {
  hasKnownAccess: boolean;
  hasAccess: boolean;
  requiresUpgrade: boolean;
};

type WikiGenerationErrorPayload = {
  code?: string;
  error?: string;
  message?: string;
};

export function getWikiGenerationAccessState({
  isPublicRepo,
  hasActiveSubscription,
  isSettingsLoading,
}: WikiGenerationAccessStateParams): WikiGenerationAccessState {
  const hasKnownAccess =
    isPublicRepo || (!isSettingsLoading && hasActiveSubscription !== undefined);
  const hasAccess = isPublicRepo || hasActiveSubscription === true;

  return {
    hasKnownAccess,
    hasAccess,
    requiresUpgrade: hasKnownAccess && !hasAccess,
  };
}

export async function parseWikiGenerationErrorResponse(
  response: Response,
  isRetry: boolean,
): Promise<{
  errorData: WikiGenerationErrorPayload | null;
  errorCode: string | undefined;
  isSubscriptionRequired: boolean;
  isTrialWikiLimitReached: boolean;
  isPrivateWikiLimitReached: boolean;
  errorMessage: string;
}> {
  let errorData: WikiGenerationErrorPayload | null = null;

  try {
    errorData = (await response.json()) as WikiGenerationErrorPayload;
  } catch {
    errorData = null;
  }

  const fallbackMessage = `Failed to ${isRetry ? 'regenerate' : 'generate'} wiki`;

  const isPrivateWikiLimitReached =
    errorData?.code === 'private_wiki_limit_reached' ||
    errorData?.code === 'trial_wiki_limit_reached';

  return {
    errorData,
    errorCode: errorData?.code,
    isSubscriptionRequired: errorData?.code === 'subscription_required',
    isTrialWikiLimitReached: errorData?.code === 'trial_wiki_limit_reached',
    isPrivateWikiLimitReached,
    errorMessage:
      errorData?.error ||
      errorData?.message ||
      (response.statusText ? `${fallbackMessage}: ${response.statusText}` : fallbackMessage),
  };
}
